import React, { useState } from 'react';
import { useTranslation } from '@ikea-customer-self-serve/gatsby-plugin-translations';
import { SparePart } from '../interfaces/Item';
import { useError } from '../contexts/Error';
import { useCart } from '../contexts/Cart';
import { useToast } from '../contexts/Toast';
import { FormFieldValues } from '../components/Form';
import { useErrorCode } from './useErrorCode';
import analytics from '../analytics';

interface OrderState {
  loading: boolean;
  quantity: number;
}

interface OrderActions {
  removeFromCart: () => void;
  addToCart: () => void;
  changeQuantity: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuantityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}
export const useCartItemQuantityController = (
  item: SparePart,
  defaultQuantity?: number
): [OrderState, OrderActions] => {
  const [loading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(defaultQuantity || 1);
  const { t } = useTranslation();
  const CartContext = useCart();
  const ErrorContext = useError();
  const ToastContext = useToast();

  const showError = (type: string, customMessage?: string): void => {
    const message = customMessage
      ? t(`spareParts.error.custom.${customMessage}`)
      : t(`spareParts.error.${type}.description`);
    ErrorContext.showTitleTextError(
      t(`spareParts.error.${type}.title`),
      message,
      t(`spareParts.error.${type}.btnText`)
    );
  };

  const removeFromCart = (): void => {
    const itemCount = CartContext.getItemCount(item.id);
    (async (): Promise<void> => {
      try {
        setIsLoading(true);
        await CartContext.removeItem(item);
        ToastContext.setToast({
          id: `remove-${item.id}`,
          text: t('spareParts.snackbar.removed', {
            name: item.id,
          }),
          buttonText: t('spareParts.snackbar.undo'),
          action: async (): Promise<void> => {
            try {
              await CartContext.addItem(item, itemCount, true);
            } catch (err) {
              showError('changeQuantity', err?.data?.message?.serviceError);
            } finally {
              ToastContext.closeToast();
            }
          },
        });
      } catch (err) {
        showError('removeFromCart');
      } finally {
        setIsLoading(false);
      }
    })();
  };
  const onQuantityChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setQuantity(parseInt(e.target.value, 10));
  };
  const changeQuantity = (e: React.ChangeEvent<HTMLInputElement>): void => {
    (async (): Promise<void> => {
      try {
        const newQuantity = parseInt(e.target.value, 10);
        setIsLoading(true);
        await CartContext.editItem(item, newQuantity);
      } catch (err) {
        showError('changeQuantity', err?.data?.message?.serviceError);
      } finally {
        setIsLoading(false);
      }
    })();
  };
  const addToCart = (): void => {
    const itemCount = CartContext.getItemCount(item.id);
    (async (): Promise<void> => {
      try {
        setIsLoading(true);
        await CartContext.addItem(item, quantity);
        ToastContext.setToast({
          id: `add-${item.id}`,
          text: t('spareParts.snackbar.added', {
            name: item.id,
          }),
          buttonText: t('spareParts.snackbar.undo'),
          action: async (): Promise<void> => {
            try {
              await CartContext.editItem(item, itemCount);
            } catch (err) {
              showError('changeQuantity', err?.data?.message?.serviceError);
            } finally {
              ToastContext.closeToast();
            }
          },
        });
      } catch (err) {
        showError('changeQuantity', err?.data?.message?.serviceError);
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return [
    { loading, quantity },
    {
      addToCart,
      removeFromCart,
      changeQuantity,
      onQuantityChange,
    },
  ];
};

interface SubmitOrderState {
  isLoading: boolean;
  isSuccess: boolean;
}
interface SubmitOrderMethods {
  onSubmitOrder: (form: FormFieldValues) => Promise<void>;
}
export const useSubmitOrder = (): [SubmitOrderState, SubmitOrderMethods] => {
  const CartContext = useCart();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { setErrorCode } = useErrorCode();

  const onSubmitOrder = async (form: FormFieldValues): Promise<void> => {
    try {
      setIsLoading(true);

      const body = {
        ...form,
      };
      delete body.terms;

      CartContext.setRecipient(body);

      await CartContext.createOrder(body);

      setIsSuccess(true);

      analytics.sendEvent({ label: 'spare-parts_completed' });
    } catch (err) {
      setErrorCode(err);
    } finally {
      setIsLoading(false);
    }
  };

  return [{ isLoading, isSuccess }, { onSubmitOrder }];
};
